import { Centrifuge, Subscription } from 'centrifuge';

export type JobStatusEvent = {
  type: 'CommissionCalculationStarted' | 'CommissionCalculationEnded' | 'CommissionCalculationFailed';
  message?: string;
  date?: string;
};

type Handler = (data: JobStatusEvent) => void;

let client: Centrifuge | null = null;
let connected = false;

const subscriptions = new Map<string, Subscription>();
const pendingSubscriptions: Array<() => void> = [];

export const connect = (): void => {
  if (client) return;

  client = new Centrifuge(process.env.REACT_APP_CENTRIFUGO_URL ?? '');

  client.on('connecting', (ctx: any) => {
    console.log('[Centrifuge] connecting', ctx.code, ctx.reason);
  });

  client.on('connected', (ctx: any) => {
    connected = true;
    console.log('[Centrifuge] connected', ctx);

    while (pendingSubscriptions.length) {
      const run = pendingSubscriptions.shift();
      run?.();
    }
  });

  client.on('disconnected', (ctx: any) => {
    connected = false;
    console.log('[Centrifuge] disconnected', ctx.code, ctx.reason);
  });

  client.on('error', (ctx: any) => {
    console.error('[Centrifuge] error', ctx);
  });

  client.connect();
};

export const disconnect = (): void => {
  subscriptions.forEach((sub) => {
    sub.unsubscribe();
    sub.removeAllListeners();

    if (client) {
      client.removeSubscription(sub);
    }
  });

  subscriptions.clear();
  pendingSubscriptions.length = 0;
  connected = false;

  client?.disconnect();
  client?.removeAllListeners();
  client = null;
};

export const subscribe = (channel: string, token: string, handler: Handler): (() => void) => {
  const runSubscribe = () => {
    if (!client) return;

    const existingSub = subscriptions.get(channel);

    if (existingSub) {
      existingSub.on('publication', (ctx: any) => {
        console.log('[Centrifuge] publication', channel, ctx.data);
        handler(ctx.data);
      });

      return;
    }

    const sub = client.newSubscription(channel, {
      data: { token },
    });

    sub.on('subscribing', (ctx: any) => {
      console.log('[Centrifuge] subscribing', channel, ctx);
    });

    sub.on('subscribed', (ctx: any) => {
      console.log('[Centrifuge] subscribed', channel, ctx);
    });

    sub.on('unsubscribed', (ctx: any) => {
      console.log('[Centrifuge] unsubscribed', channel, ctx.code, ctx.reason);
    });

    sub.on('publication', (ctx: any) => {
      console.log('[Centrifuge] publication', channel, ctx.data);
      handler(ctx.data);
    });

    sub.on('error', (ctx: any) => {
      console.error('[Centrifuge] subscription error', channel, ctx);
    });

    subscriptions.set(channel, sub);
    sub.subscribe();
  };

  if (connected) {
    runSubscribe();
  } else {
    pendingSubscriptions.push(runSubscribe);
  }

  return () => {
    const sub = subscriptions.get(channel);

    if (!sub || !client) return;

    sub.unsubscribe();
    sub.removeAllListeners();

    client.removeSubscription(sub);
    subscriptions.delete(channel);
  };
};
